// Classify a session-refresh failure as a DEFINITIVE rejection of the credential
// vs a TRANSPORT failure (DS4-0054).
//
// The restore path refreshes an expired token at boot. The old code destroyed the
// local session on ANY refresh failure, so a network blip - common on mobile -
// signed the user out. Only a real HTTP rejection of the refresh token proves the
// session is invalid; anything else is unknown, and the safe move is to RETAIN local
// state and retry. Keeping a genuinely dead session locally is harmless: every
// request still fails closed server-side, and the next refresh that reaches the
// server clears it. Destroying a live session on a blip is user-visible damage.
//
// `/session/refresh` answers 401 (session_revoked / invalid_grant) when the token is
// dead. 400 (bad request), 408/429 (transient), and 5xx are NOT proof the session is
// invalid, and a network/DNS/timeout error carries no HTTP response at all.
//
// This is the single classifier for every refresh path: the per-request freshness
// check (auth-token-freshness) and the boot-time restore paths (web + RN session
// managers) all import it, so "definitive" means the same thing everywhere. It lives
// in this leaf module to avoid an import cycle between those consumers.
export function isDefinitiveRejection(err) {
    var _a;
    const status = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status;
    return status === 401 || status === 403;
}
// How long past the id token's own `exp` a session may survive purely on
// transport failures.
//
// "Retain on a blip" must not become "retain forever". A statusless failure is
// unknown, so the first ones are absorbed - but a revoked session, or a gateway
// that rewrites the issuer's 401 into a 502, would otherwise keep
// isAuthenticated() reporting a signed-in user for as long as the device never
// reaches the issuer again. One day covers an offline flight or a bad-network day
// while making the retention finite: past it, an unrefreshable session is cleared
// and the user is asked to sign in again.
export const TRANSPORT_RETENTION_GRACE_MS = 24 * 60 * 60 * 1000;
/**
 * May a session whose refresh failed WITHOUT a definitive rejection be kept?
 *
 * `expSeconds` is the id token's `exp` claim. Fails closed: a missing or
 * non-numeric expiry is not a usable bound, so such a session is never retained.
 */
export function mayRetainAfterTransportFailure(expSeconds, now = Date.now()) {
    if (typeof expSeconds !== 'number' || !Number.isFinite(expSeconds))
        return false;
    return now <= expSeconds * 1000 + TRANSPORT_RETENTION_GRACE_MS;
}
